import { css } from '@emotion/react';
import {
  Banner,
  Button,
  Card,
  color,
  Icon,
  iconSize,
  spacing,
  Typography
} from '@uniquegood/realworld-studio-design';
import { RowFlex, JustifySpaceBetween } from '@/styles';

interface MissionCompletionData {
  missionId: string;
  missionName: string;
  missionThumbnail?: string;
  isLocked: boolean;
  isVisible: boolean;
  questCount: number;
  completionRate: number;
  completedUsers: number;
  totalUsers: number;
}

interface MissionCompletionSectionProps {
  onUpgradeClick?: () => void;
}

const MOCK_DATA: MissionCompletionData[] = [
  {
    missionId: '1',
    missionName: '미션 1',
    missionThumbnail: 'https://via.placeholder.com/32',
    isLocked: false,
    isVisible: false,
    questCount: 2,
    completionRate: 100,
    completedUsers: 1234,
    totalUsers: 1234
  },
  {
    missionId: '2',
    missionName: '미션 2',
    missionThumbnail: 'https://via.placeholder.com/32',
    isLocked: false,
    isVisible: false,
    questCount: 2,
    completionRate: 100,
    completedUsers: 856,
    totalUsers: 856
  },
  {
    missionId: '3',
    missionName: '미션 3',
    missionThumbnail: 'https://via.placeholder.com/32',
    isLocked: false,
    isVisible: false,
    questCount: 2,
    completionRate: 1.8,
    completedUsers: 23,
    totalUsers: 1278
  },
  {
    missionId: '4',
    missionName: '미션 4',
    missionThumbnail: 'https://via.placeholder.com/32',
    isLocked: false,
    isVisible: false,
    questCount: 2,
    completionRate: 100,
    completedUsers: 945,
    totalUsers: 945
  },
  {
    missionId: '5',
    missionName: '미션 5',
    missionThumbnail: 'https://via.placeholder.com/32',
    isLocked: false,
    isVisible: false,
    questCount: 2,
    completionRate: 100,
    completedUsers: 678,
    totalUsers: 678
  }
];

export default function MissionCompletionSection({
  onUpgradeClick
}: MissionCompletionSectionProps) {
  return (
    <Card cssStyle={CardStyle}>
      <div css={[RowFlex, JustifySpaceBetween, HeaderStyle]}>
        <Typography as="h3" type="heading">
          미션별 완료율
        </Typography>
        <Button icon="download_solid" type="plain" disabled>
          다운로드
        </Button>
      </div>
      <Banner
        type="informational"
        header={
          <Typography type="heading">현재 요금제에서는 미션별 완료율을 볼 수 없어요.</Typography>
        }
      >
        <>
          <Typography type="body">데이터를 확인하려면 요금제를 업그레이드해 주세요.</Typography>
          <Button
            type="primary"
            size="medium"
            onClick={onUpgradeClick}
            cssStyle={UpgradeButtonStyle}
          >
            요금제 업그레이드
          </Button>
        </>
      </Banner>

      {/* 블러 처리된 테이블 */}
      <div css={BlurredTableWrapperStyle}>
        <table css={TableStyle}>
          <thead>
            <tr>
              <th style={{ width: '57px' }}>미션</th>
              <th>이름</th>
              <th>기본 잠금 여부</th>
              <th>기본 노출 여부</th>
              <th>포함 퀘스트 수</th>
              <th style={{ width: '128px' }}>완료 수</th>
              <th style={{ width: '112px' }}>완료율</th>
            </tr>
          </thead>
          <tbody>
            {MOCK_DATA.map((mission) => (
              <tr key={mission.missionId}>
                <td>
                  <div css={ThumbnailCellStyle}>
                    {mission.missionThumbnail ? (
                      <img
                        src={mission.missionThumbnail}
                        alt={mission.missionName}
                        css={ThumbnailStyle}
                      />
                    ) : (
                      <div css={ThumbnailPlaceholderStyle} />
                    )}
                  </div>
                </td>
                <td>
                  <Typography type="body">{mission.missionName}</Typography>
                </td>
                <td>
                  <Typography type="body">{mission.isLocked ? '잠금' : '미잠금'}</Typography>
                </td>
                <td>
                  <Typography type="body">{mission.isVisible ? '노출' : '미노출'}</Typography>
                </td>
                <td>
                  <Typography type="body">{mission.questCount}</Typography>
                </td>
                <td>
                  <Typography type="body">{mission.completedUsers}</Typography>
                </td>
                <td>
                  <div css={CompletionRateCellStyle}>
                    <div css={CompletionRateTagStyle(mission.completionRate)}>
                      <Icon
                        icon="check_solid"
                        size={iconSize.small}
                        color={mission.completionRate >= 100 ? '#00cc18' : color.text_subdued}
                      />
                      <Typography type="body">{mission.completionRate.toFixed(1)}%</Typography>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const CardStyle = css`
  max-width: 1300px;
  min-width: 740px;
  margin-bottom: ${spacing.margin.xlarge2};
`;

const HeaderStyle = css`
  margin-bottom: ${spacing.margin.xlarge2};
  align-items: center;
`;

const TableStyle = css`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.medium};
  overflow: hidden;

  th,
  td {
    padding: ${spacing.padding.medium};
    text-align: left;
    border-bottom: 1px solid ${color.surface_default_subdued};
  }

  th {
    background-color: ${color.surface_default_subdued};
    color: ${color.text_default};
    font-weight: 500;
    font-size: 14px;
  }

  tbody tr:hover {
    background-color: ${color.surface_default_hovered};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ThumbnailCellStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.padding.small};
`;

const ThumbnailStyle = css`
  width: 32px;
  height: 32px;
  border-radius: ${spacing.borderRadius.small};
  object-fit: cover;
`;

const ThumbnailPlaceholderStyle = css`
  width: 32px;
  height: 32px;
  border-radius: ${spacing.borderRadius.small};
  background-color: ${color.surface_default_subdued};
`;

const CompletionRateCellStyle = css`
  display: flex;
  align-items: center;
  gap: ${spacing.margin.xsmall};
`;

const CompletionRateTagStyle = (rate: number) => css`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px 6px 8px;
  border-radius: ${spacing.borderRadius.medium};
  background-color: ${rate >= 100 ? 'rgba(229, 255, 232, 1)' : color.surface_default_subdued};

  span {
    color: ${rate >= 100 ? '#00cc18' : color.text_subdued};
    font-weight: 500;
  }
`;

const UpgradeButtonStyle = css`
  margin-top: ${spacing.margin.medium};
  align-self: flex-start;
  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
  border: none;
  border-radius: ${spacing.borderRadius.medium};
  padding: ${spacing.padding.small} ${spacing.padding.medium};
  color: ${color.white};
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const BlurredTableWrapperStyle = css`
  margin-top: ${spacing.margin.xlarge2};
  filter: blur(10px);
  pointer-events: none;
  user-select: none;
`;
